/**
 * Хук для обработки drag & drop файлов
 */

import { useEffect, useState, useCallback } from 'react';
import { chatLogger } from '@/utils/chatLogger';

/**
 * Опции для useDragDrop
 */
interface DragDropOptions {
  /** Колбек при drop файла */
  onFileDrop: (file: File) => void;
  /** Разрешенные типы файлов */
  acceptedTypes?: string[];
}

/**
 * Результат хука useDragDrop
 */
interface DragDropResult {
  /** Сейчас над зоной drop */
  isDragging: boolean;
}

/**
 * Хук для обработки drag & drop файлов
 * 
 * @description Обрабатывает перетаскивание файлов в элемент
 */
export function useDragDrop(
  elementRef: React.RefObject<HTMLElement>,
  options: DragDropOptions
): DragDropResult {
  const { onFileDrop, acceptedTypes = ['image/*'] } = options;
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Проверяем что покинули элемент (не дочерний элемент)
    const rect = elementRef.current?.getBoundingClientRect();
    if (rect && (e.clientX <= rect.left || e.clientX >= rect.right || 
                  e.clientY <= rect.top || e.clientY >= rect.bottom)) {
      setIsDragging(false);
    }
  }, [elementRef]);

  const handleDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = e.dataTransfer?.files;
      if (!files || files.length === 0) return;

      const file = files[0];

      // Проверка типа файла
      const isAccepted = acceptedTypes.some((type) => {
        if (type.endsWith('/*')) {
          const category = type.split('/')[0];
          return file.type.startsWith(category + '/');
        }
        return file.type === type;
      });

      if (!isAccepted) {
        chatLogger.warn('[DragDrop] Тип файла не поддерживается:', file.type);
        return;
      }

      onFileDrop(file);
    },
    [acceptedTypes, onFileDrop]
  );

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    element.addEventListener('dragover', handleDragOver);
    element.addEventListener('dragleave', handleDragLeave);
    element.addEventListener('drop', handleDrop);

    return () => {
      element.removeEventListener('dragover', handleDragOver);
      element.removeEventListener('dragleave', handleDragLeave);
      element.removeEventListener('drop', handleDrop);
    };
  }, [elementRef, handleDragOver, handleDragLeave, handleDrop]);

  return { isDragging };
}

